<template>
  <div class="directory-tree">
    <div class="directory-header">
      <h4>{{ title }}</h4>
      <div class="directory-controls">
        <button @click="expandAll" class="expand-all-btn">Expand All</button>
        <button @click="collapseAll" class="collapse-all-btn">Collapse All</button>
      </div>
    </div>
    <div class="directory-content">
      <div class="tree-container">
        <div 
          v-for="item in treeItems" 
          :key="item.id"
          class="tree-item"
          :style="{ marginLeft: `${item.level * 20}px` }"
        >
          <div 
            v-if="item.isFolder"
            class="folder-item"
            @click="toggleFolder(item.id)"
          >
            <span class="folder-icon" :class="{ collapsed: !item.isExpanded }">
              {{ item.isExpanded ? '▼' : '▶' }}
            </span>
            <span class="folder-name">{{ item.name }}</span>
            <span v-if="item.description" class="folder-description"># {{ item.description }}</span>
          </div>
          <div v-else class="file-item">
            <span class="file-icon">📄</span>
            <span class="file-name">{{ item.name }}</span>
            <span v-if="item.description" class="file-description"># {{ item.description }}</span>
          </div>
          
          <div v-if="item.isFolder && item.isExpanded" class="folder-children">
            <div 
              v-for="child in item.children" 
              :key="child.id"
              class="tree-item"
              :style="{ marginLeft: `${child.level * 20}px` }"
            >
              <div 
                v-if="child.isFolder"
                class="folder-item"
                @click="toggleFolder(child.id)"
              >
                <span class="folder-icon" :class="{ collapsed: !child.isExpanded }">
                  {{ child.isExpanded ? '▼' : '▶' }}
                </span>
                <span class="folder-name">{{ child.name }}</span>
                <span v-if="child.description" class="folder-description"># {{ child.description }}</span>
              </div>
              <div v-else class="file-item">
                <span class="file-icon">📄</span>
                <span class="file-name">{{ child.name }}</span>
                <span v-if="child.description" class="file-description"># {{ child.description }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

interface TreeItem {
  id: string
  name: string
  description: string
  level: number
  isFolder: boolean
  isExpanded: boolean
  children: TreeItem[]
}

interface Props {
  content: string
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Directory Structure'
})

const treeItems = ref<TreeItem[]>([])

// Parse the simple indented directory structure
function parseDirectoryStructure(content: string): TreeItem[] {
  const lines = content.trim().split('\n')
  const items: TreeItem[] = []
  const stack: TreeItem[] = []
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    
    // Calculate indentation level (2 spaces = 1 level)
    let level = 0
    let originalLine = lines[i]
    while (originalLine.startsWith('  ')) {
      level++
      originalLine = originalLine.substring(2)
    }
    
    // Extract name and description
    let name = originalLine.trim()
    let description = ''
    
    if (name.includes('#')) {
      const parts = name.split('#')
      name = parts[0].trim()
      description = parts.slice(1).join('#').trim()
    }
    
    // Determine if it's a folder
    const isFolder = name.endsWith('/') || name.endsWith('\\')
    
    const item: TreeItem = {
      id: `item-${i}`,
      name,
      description,
      level,
      isFolder,
      isExpanded: false,
      children: []
    }
    
    // Add to appropriate parent or root
    if (level === 0) {
      items.push(item)
      stack.length = 0
      stack.push(item)
    } else {
      // Find the correct parent at this level
      while (stack.length > level) {
        stack.pop()
      }
      
      if (stack.length > 0) {
        const parent = stack[stack.length - 1]
        parent.children.push(item)
      }
      
      stack.push(item)
    }
  }
  
  return items
}

function toggleFolder(id: string) {
  const item = findItem(treeItems.value, id)
  if (item) {
    item.isExpanded = !item.isExpanded
  }
}

function findItem(items: TreeItem[], id: string): TreeItem | null {
  for (const item of items) {
    if (item.id === id) return item
    if (item.children.length > 0) {
      const found = findItem(item.children, id)
      if (found) return found
    }
  }
  return null
}

function expandAll() {
  expandAllItems(treeItems.value, true)
}

function collapseAll() {
  expandAllItems(treeItems.value, false)
}

function expandAllItems(items: TreeItem[], expand: boolean) {
  for (const item of items) {
    if (item.isFolder) {
      item.isExpanded = expand
    }
    if (item.children.length > 0) {
      expandAllItems(item.children, expand)
    }
  }
}

onMounted(() => {
  if (props.content) {
    treeItems.value = parseDirectoryStructure(props.content)
  }
})
</script>

<style scoped>
.directory-tree {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin: 1rem 0;
}

.directory-header {
  background-color: #f9fafb;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.directory-header h4 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.directory-controls {
  display: flex;
  gap: 0.5rem;
}

.expand-all-btn,
.collapse-all-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: white;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.expand-all-btn:hover,
.collapse-all-btn:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.directory-content {
  padding: 1rem;
  background-color: white;
}

.tree-container {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
}

.tree-item {
  margin: 0.25rem 0;
}

.folder-item,
.file-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.folder-item:hover {
  background-color: #f3f4f6;
}

.file-item:hover {
  background-color: #f9fafb;
}

.folder-icon {
  color: #059669;
  font-weight: bold;
  min-width: 1rem;
  text-align: center;
}

.folder-icon.collapsed {
  color: #6b7280;
}

.file-icon {
  color: #6b7280;
  min-width: 1rem;
  text-align: center;
}

.folder-name {
  color: #059669;
  font-weight: 500;
}

.file-name {
  color: #374151;
}

.folder-description,
.file-description {
  color: #6b7280;
  font-style: italic;
}

.folder-children {
  margin-left: 1rem;
  border-left: 1px solid #e5e7eb;
  padding-left: 1rem;
}
</style>

